import { useCallback, useEffect, useState } from 'react';

/**
 * Hook for optimistic updates.
 *
 * This hook allows you to do optimistic updates of a value which is externally
 * managed. When the value should be changed (e.g. after a user interaction),
 * the new value is set immediately and the external change handler is called.
 *
 * This is useful if React/the browser is not able to associate a user
 * interaction with a value update, for instance if the user is writing at the
 * start of a text field and the caret position is reset to the end.
 *
 * @param value The external value
 * @param onValueChange The external value change handler
 * @returns A tuple containing the immediate value and the update function
 */
export const useOptimistic = <Value, UpdatedValue>(
  value: Value,
  onValueChange: (value: UpdatedValue) => void
) => {
  const [internalValue, setInternalValue] = useState<Value | UpdatedValue>(
    value
  );

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const updateValue = useCallback(
    (value: UpdatedValue) => {
      setInternalValue(value);
      onValueChange(value);
    },
    [setInternalValue, onValueChange]
  );

  return [internalValue, updateValue] as const;
};
